import { ActionButton } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Button, CircularProgress, Grid, Paper, Typography } from '@mui/material'
import { debounce } from 'lodash'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import { FiltersContainer as Filters, SearchFilter } from '../../components/filters'
import { CampaignListSlice } from '../../reducers'
import { routes } from '../../routes'
import CampaignItem from './campaign-item/campaign-item'
import { useStyles } from './style'

const CampaignList = () => {
  const classes = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const initialFilters = React.useMemo(() => ({ search: '' }), [])
  const [filters, setFilters] = React.useState(initialFilters)
  const pageSize = React.useMemo(() => 10, [])

  const campaignList = useSelector(CampaignListSlice.selectors.selectCampaignList)
  const campaignTotal = useSelector(CampaignListSlice.selectors.selectTotal)
  const lastKey = useSelector(CampaignListSlice.selectors.lastKey)

  const hasMoreCampaigns = React.useMemo(() => campaignTotal > campaignList.length, [campaignList, campaignTotal])

  const [getCampaignList, addCampaignList] = useEvent([
    CampaignListSlice.eventBus.getCampaignList,
    CampaignListSlice.eventBus.addCampaignList
  ])

  const onFetchMoreCampaigns = React.useCallback(() => {
    addCampaignList.trigger({
      lastKey,
      limit: pageSize,
      filters
    })
  }, [addCampaignList, pageSize, filters, lastKey])

  const isLoading = React.useMemo(() => {
    return getCampaignList.isWorking || addCampaignList.isWorking || !getCampaignList.hasBeenTriggered
  }, [getCampaignList.isWorking, getCampaignList.hasBeenTriggered, addCampaignList.isWorking])

  const debouncedSearch = React.useMemo(() => debounce(filters => {
    getCampaignList.trigger({ limit: pageSize, filters })
  }, 1000), [getCampaignList, pageSize])

  React.useEffect(() => { debouncedSearch(filters) }, [debouncedSearch, filters])

  React.useEffect(() => () => dispatch(CampaignListSlice.actions.resetCampaignList()), [dispatch])

  const handleClearFilters = React.useCallback(() => setFilters(initialFilters), [setFilters, initialFilters])

  const handleCampaignClick = React.useCallback(campaign => {
    navigate(generatePath(routes.campaignDetails.path, { id: campaign.sk }), { state: { campaign } })
  }, [navigate])

  const handleCampaignKeyPress = React.useCallback((e, tenant) => e.which === 13 && handleCampaignClick(tenant), [handleCampaignClick])

  return (
    <>
      <Grid container classes={{ root: classes.header }}>
        <Grid container direction='row' alignItems='center'>
          <Grid item style={{ flexGrow: 1 }}>
            <Typography id='campaign-list-title' tabIndex={0} component='div' variant='h5'>
            Campaigns
              {!!campaignTotal && <Typography component='span' variant='subtitle1' className={classes.contentTotal}> {`(${campaignTotal})`} </Typography>}
            </Typography>
          </Grid>
          <Filters filters={filters} onChange={setFilters} onClear={handleClearFilters}>
            <SearchFilter name='search' />
          </Filters>
          <Grid item>
            <ActionButton
              id='create-campaign-button'
              variant='primary'
              name='create-campaign'
              aria-label='create new campaign'
              onClick={() => navigate(routes.campaignNew.path)}
            > New Campaign
            </ActionButton>
          </Grid>
        </Grid>
      </Grid>

      <Paper
        id='campaign-list'
        classes={{ root: classes.tablePaper }}
        tabIndex={0}
        aria-label='Campaign List'
        elevation={0}
      >
        <Grid
          id='campaign-list-headers'
          container
          tabIndex={0}
          direction='row'
          alignItems='center'
          className={classes.gridHeader}
          classes={{ root: classes.tableHead }}
          aria-label='List headers, Title, Type, Last update'
        >
          <Grid item md={4}>Name</Grid>
          <Grid item md={2}>Audience</Grid>
          <Grid item md={1}>Start Date</Grid>
          <Grid item md={1}>End Date</Grid>
          <Grid item md={2}>Status</Grid>
          <Grid item md={2}>Creation Date</Grid>
        </Grid>
        {!isLoading && campaignList.map((row, key) => (
          <CampaignItem
            key={key}
            index={key}
            campaign={row}
            classes={classes}
            lastOne={key === campaignList.length - 1}
            onClick={handleCampaignClick}
            onKeyPress={handleCampaignKeyPress}
          />
        ))}
        {isLoading && (
          <Grid
            id='campaign-list-loading'
            container
            direction='row'
            justifyContent='center'
            alignItems='center'
            classes={{ root: classes.loadingAnimation }}
          >
            <CircularProgress size={60} color='primary' />
          </Grid>
        )}
        {!isLoading && !campaignList.length && (
          <Grid
            id='campaign-list-empty'
            container
            direction='row'
            justifyContent='center'
            alignContent='center'
            classes={{ root: classes.emptyList }}
          >
            <Typography variant='h5'> No campaign found! </Typography>
          </Grid>
        )}
      </Paper>
      {!isLoading && hasMoreCampaigns && (
        <Grid
          id='campaign-list-load-more'
          container
          direction='row'
          justifyContent='center'
          align='center'
          classes={{ root: classes.loadMoreBtn }}
        >
          <Grid item xs={12}>
            <Button variant='contained' color='primary' name='' onClick={onFetchMoreCampaigns}>
              Load More
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  )
}

export default CampaignList
